import type { ReactNode } from 'react';
import {
  ArrowLeftIcon,
  BellIcon,
  ChevronDownIcon,
  HelpCircleIcon,
  LogOutIcon,
  MenuIcon,
  MessageSquareIcon,
  SettingsIcon,
  SparklesIcon,
  UserRoundIcon,
} from 'lucide-react';

/**
 * Gedeelde schil van het nagebouwde Therapieland-zorgverlenerportaal
 * (app.therapieland.nl) voor de sales-etalage op /e-health.
 *
 * Nagebouwd naar de screenshots in docs/designs/screenshots-therapieland/.
 * Alle kleuren zijn uit die PNG's gesampled, niet geschat. Die map staat NIET in
 * de repo: er staan patiëntgegevens op.
 *
 * Alle data is fictief en hardcoded; er zijn geen netwerkcalls.
 */

/** Uit de screenshots gesamplede Therapieland-huisstijl. */
export const TL = {
  /** Primair: logo, knoppen, links, actieve onderstreping. */
  primair: '#086d93',
  /** Aqua van de Therapieland-iconen, gemeten in de icoonbestanden zelf
      (public/therapieland/). Afwijkend van TL.primair, dat de knoppen en links
      kleurt. */
  icoon: '#00a2c1',
  /** Achtergrond van het actieve menu-item in de zijbalk. */
  navActief: '#adccd4',
  /** Zijbalk, tabelkoppen, kaartkopbanden, patiënt-headerblok. */
  panel: '#f8f8f9',
  /** Pil 'Vraag de Assistent'. */
  assistent: '#e1edf2',
  rand: '#e5e5e5',
  /** Scheidingslijn in de zijbalk; donkerder dan de kaartrand, anders valt hij
      weg tegen TL.panel. Gemeten op de lijn boven het gebruikersblok. */
  randSterk: '#c3c5c8',
  /** Koppen. */
  ink: '#212426',
  /** Lopende tekst. */
  body: '#484d52',
  muted: '#5b5b5b',
  zacht: '#a4a6a9',
  groen: '#83c06e',
  groenInk: '#406e2f',
  /** Voortgangsbalken en vinkjes in het programma-voortgangsscherm; dieper dan
      het groen van de dashboard-donut. */
  groenBalk: '#579a41',
  /** Stip op een stapovergang in een voortgangsbalk. */
  groenStip: '#477739',
  /** Lichtblauwe rijachtergrond in het voortgangsscherm. */
  stapRij: '#f2f8fa',
  oranje: '#fea262',
  rood: '#d32f2f',
  chip: '#e0e0e0',
};

export interface TlPatient {
  id: string;
  naam: string;
  initialen: string;
  email: string;
  gebdatum: string;
  geslacht: string;
  startDatum: string;
  laatsteInlog: string;
  nieuweActies: number;
}

// Demopatiënten. Onmiskenbaar fictief, met dezelfde naamvorm als in de
// screenshots zodat de kolommen even breed vullen.
export const TL_PATIENTEN: TlPatient[] = [
  {
    id: 'proef',
    naam: 'Pieter Proefpatiënt',
    initialen: 'PP',
    email: 'p.proefpatient@demo.nl',
    gebdatum: '13/03/1962',
    geslacht: '-',
    startDatum: '18/07/2017',
    laatsteInlog: '19/08/2026',
    nieuweActies: 2,
  },
  {
    id: 'voorbeeld',
    naam: 'Sanne van der Voorbeeld',
    initialen: 'SV',
    email: 's.vandervoorbeeld@demo.nl',
    gebdatum: '23/01/1979',
    geslacht: 'Vrouw',
    startDatum: '04/02/2019',
    laatsteInlog: '18/08/2026',
    nieuweActies: 7,
  },
  {
    id: 'demo',
    naam: 'Daan van der Demo',
    initialen: 'DD',
    email: 'd.vanderdemo@demo.nl',
    gebdatum: '19/10/1990',
    geslacht: 'Man',
    startDatum: '11/11/2020',
    laatsteInlog: '12/08/2026',
    nieuweActies: 4,
  },
  {
    id: 'test',
    naam: 'Bregje Testpersoon',
    initialen: 'BT',
    email: 'b.testpersoon@demo.nl',
    gebdatum: '11/02/1972',
    geslacht: 'Vrouw',
    startDatum: '02/06/2021',
    laatsteInlog: '09/08/2026',
    nieuweActies: 2,
  },
  {
    id: 'kees',
    naam: 'Kees Demopatiënt',
    initialen: 'KD',
    email: 'k.demopatient@demo.nl',
    gebdatum: '25/02/1985',
    geslacht: 'Man',
    startDatum: '30/09/2022',
    laatsteInlog: '01/08/2026',
    nieuweActies: 3,
  },
];

export const TL_ZORGVERLENER = {
  naam: 'Tessa Testarts',
  praktijk: 'Huisartsenpraktijk Demo',
  praktijkInitialen: 'HD',
  initialen: 'TT',
};

/** Component-vorm van de nagebouwde SVG-iconen hieronder. */
type IcoonComponent = (props: { size?: number }) => JSX.Element;

/**
 * De drie zijbalk-iconen van het portaal, als SVG nagebouwd.
 *
 * Dashboard en Patiënten zijn Material Design Icons (home-variant en
 * account-group), dezelfde set die het portaal gebruikt: de DOM toont daar
 * `class="mdi-account-group"`. Programma's is een eigen icoon van Therapieland
 * (`<use href="#custom-programs-outline">`) en is nagetekend uit een uitsnede
 * van 31x31 pixels: een ring met drie verbonden knooppunten. De ring vult het
 * hele vak van 24x24, anders dan de twee MDI-iconen ernaast (16x18 en 24x14
 * aan inkt); dat is in het portaal gemeten en niet geschat.
 */
function HomeVariantIcon({ size = 21 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12,3L20,9V21H15V14H9V21H4V9L12,3Z" />
    </svg>
  );
}

function AccountGroupIcon({ size = 21 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12,5.5A3.5,3.5 0 0,1 15.5,9A3.5,3.5 0 0,1 12,12.5A3.5,3.5 0 0,1 8.5,9A3.5,3.5 0 0,1 12,5.5M5,8C5.56,8 6.08,8.15 6.53,8.42C6.38,9.85 6.8,11.27 7.66,12.38C7.16,13.34 6.16,14 5,14A3,3 0 0,1 2,11A3,3 0 0,1 5,8M19,8A3,3 0 0,1 22,11A3,3 0 0,1 19,14C17.84,14 16.84,13.34 16.34,12.38C17.2,11.27 17.62,9.85 17.47,8.42C17.92,8.15 18.44,8 19,8M5.5,18.25C5.5,16.18 8.41,14.5 12,14.5C15.59,14.5 18.5,16.18 18.5,18.25V20H5.5V18.25M0,20V18.5C0,17.11 1.89,15.94 4.45,15.6C3.86,16.28 3.5,17.22 3.5,18.25V20H0M24,20H20.5V18.25C20.5,17.22 20.14,16.28 19.55,15.6C22.11,15.94 24,17.11 24,18.5V20Z" />
    </svg>
  );
}

function ProgrammasIcon({ size = 21 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10.8" stroke="currentColor" strokeWidth="2.4" />
      <path
        d="M7.5 12 L14.5 7 M7.5 12 L15 17 M14.5 7 L15 17"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <circle cx="7.5" cy="12" r="2.3" fill="currentColor" />
      <circle cx="14.5" cy="7" r="2.3" fill="currentColor" />
      <circle cx="15" cy="17" r="2.3" fill="currentColor" />
    </svg>
  );
}

export type TlNav =
  | 'dashboard'
  | 'patienten'
  | 'programmas'
  | 'beeldbellen'
  | 'chat'
  | 'zelfhulp'
  | 'vragenlijsten'
  | 'monitoring';

/**
 * De zijbalk van het echte portaal kent alleen Dashboard, Patiënten en
 * Programma's. Daaronder staan de overige modules uit
 * opties-binnen-therapieland.png, zodat het volledige aanbod klikbaar is.
 * Bewust zonder scheidingslijn: het portaal toont één doorlopende lijst.
 *
 * De moduleknoppen gebruiken de echte icoonbestanden van therapieland.nl
 * (public/therapieland/), dezelfde als in dat menu. De drie portaal-items
 * hebben geen los icoonbestand en blijven lucide-benaderingen in TL.primair.
 */
interface NavItem {
  key: TlNav;
  label: string;
  /** Nagebouwd SVG-icoon van het portaal zelf. */
  Icon?: IcoonComponent;
  /** Pad naar het echte Therapieland-icoon in public/therapieland/. */
  icoon?: string;
}

const NAV_ECHT: NavItem[] = [
  { key: 'dashboard', label: 'Dashboard', Icon: HomeVariantIcon },
  { key: 'patienten', label: 'Patiënten', Icon: AccountGroupIcon },
  { key: 'programmas', label: "Programma's", Icon: ProgrammasIcon },
];

const NAV_MODULES: NavItem[] = [
  { key: 'beeldbellen', label: 'Beeldbellen', icoon: '/therapieland/beeldbellen.png' },
  { key: 'chat', label: 'Chat', icoon: '/therapieland/chat.png' },
  { key: 'zelfhulp', label: 'Zelfhulpportaal', icoon: '/therapieland/zelfhulpportaal.png' },
  { key: 'vragenlijsten', label: 'Vragenlijsten', icoon: '/therapieland/vragenlijsten.png' },
  { key: 'monitoring', label: 'Monitoring', icoon: '/therapieland/monitoring.png' },
];

export function TlShell({
  nav,
  setNav,
  children,
}: {
  nav: TlNav;
  setNav: (n: TlNav) => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white">
      <TlTopBar />
      <div className="flex min-h-0 flex-1">
        <TlSidebar nav={nav} setNav={setNav} />
        <div className="min-w-0 flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function TlTopBar(): JSX.Element {
  return (
    <div
      className="flex h-[72px] shrink-0 items-center justify-between bg-white pl-6 pr-7"
      style={{ borderBottom: `1px solid ${TL.rand}` }}
    >
      <div className="flex items-center gap-8">
        <MenuIcon size={24} style={{ color: TL.ink }} />
        <img src="/therapieland-logo.png" alt="Therapieland" className="h-[26px] w-auto" />
      </div>

      <div className="flex items-center gap-5">
        <span
          className="flex items-center gap-2 rounded px-4 py-2 text-[14.5px] font-semibold"
          style={{ backgroundColor: TL.assistent, color: TL.primair }}
        >
          <SparklesIcon size={16} /> Vraag de Assistent
        </span>
        <TlBadgeIcon Icon={BellIcon} aantal={24} />
        <TlBadgeIcon Icon={MessageSquareIcon} aantal={3} />
        <span
          className="flex h-[30px] w-[30px] items-center justify-center rounded text-[13px] font-semibold"
          style={{ backgroundColor: TL.chip, color: TL.body }}
        >
          {TL_ZORGVERLENER.praktijkInitialen}
        </span>
        <span className="text-[16px]" style={{ color: TL.ink }}>
          {TL_ZORGVERLENER.praktijk}
        </span>
        <ChevronDownIcon size={20} style={{ color: TL.ink }} />
      </div>
    </div>
  );
}

function TlBadgeIcon({ Icon, aantal }: { Icon: typeof BellIcon; aantal: number }): JSX.Element {
  return (
    <span className="relative">
      <Icon size={22} strokeWidth={1.6} style={{ color: TL.ink }} />
      <span
        className="absolute -right-2 -top-2 flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[11px] font-semibold text-white"
        style={{ backgroundColor: TL.rood }}
      >
        {aantal}
      </span>
    </span>
  );
}

function TlSidebar({ nav, setNav }: { nav: TlNav; setNav: (n: TlNav) => void }): JSX.Element {
  return (
    <aside
      className="flex w-[284px] shrink-0 flex-col justify-between overflow-y-auto"
      style={{ backgroundColor: TL.panel, borderRight: `1px solid ${TL.rand}` }}
    >
      <nav className="pt-2">
        {NAV_ECHT.map((i) => (
          <TlNavItem key={i.key} item={i} actief={nav === i.key} onKies={() => setNav(i.key)} />
        ))}
        {NAV_MODULES.map((i) => (
          <TlNavItem key={i.key} item={i} actief={nav === i.key} onKies={() => setNav(i.key)} />
        ))}
      </nav>

      <div className="pb-4 pt-6" style={{ borderTop: `1px solid ${TL.randSterk}` }}>
        <span className="flex items-center gap-3.5 px-6 py-2.5">
          <span
            className="flex h-[26px] w-[26px] items-center justify-center rounded-full"
            style={{ backgroundColor: TL.primair }}
          >
            <UserRoundIcon size={15} className="text-white" />
          </span>
          <span className="text-[15.5px] font-semibold" style={{ color: TL.ink }}>
            {TL_ZORGVERLENER.naam}
          </span>
        </span>
        {[
          { label: 'Help', Icon: HelpCircleIcon },
          { label: 'Instellingen', Icon: SettingsIcon },
          { label: 'Uitloggen', Icon: LogOutIcon },
        ].map(({ label, Icon }) => (
          <span key={label} className="flex items-center gap-3.5 px-6 py-2.5">
            <Icon size={20} strokeWidth={1.7} style={{ color: TL.ink }} />
            <span className="text-[15.5px]" style={{ color: TL.ink }}>
              {label}
            </span>
          </span>
        ))}
      </div>
    </aside>
  );
}

function TlNavItem({
  item,
  actief,
  onKies,
}: {
  item: NavItem;
  actief: boolean;
  onKies: () => void;
}): JSX.Element {
  const { label, Icon, icoon } = item;
  return (
    <button
      type="button"
      onClick={onKies}
      className="flex w-full items-center gap-3.5 px-6 py-2.5 text-left"
      style={actief ? { backgroundColor: TL.navActief } : undefined}
    >
      {icoon ? (
        <img src={icoon} alt="" className="h-[21px] w-[21px] shrink-0 object-contain" />
      ) : (
        Icon && (
          <span className="shrink-0 leading-none" style={{ color: TL.icoon }}>
            <Icon size={21} />
          </span>
        )
      )}
      <span className="text-[15.5px]" style={{ color: TL.ink, fontWeight: actief ? 600 : 400 }}>
        {label}
      </span>
    </button>
  );
}

/** Inhoudskolom: in het portaal staat die links uitgelijnd met ruime marge. */
export function TlWrap({ children }: { children: ReactNode }): JSX.Element {
  return <div className="mx-auto w-full max-w-[1000px] px-10 py-8">{children}</div>;
}

export function TlTerug({ label, onClick }: { label: string; onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-3 text-[15.5px]"
      style={{ color: TL.muted }}
    >
      <ArrowLeftIcon size={20} style={{ color: TL.ink }} /> {label}
    </button>
  );
}

export function TlKaart({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div className={`rounded bg-white ${className}`} style={{ border: `1px solid ${TL.rand}` }}>
      {children}
    </div>
  );
}

export function TlKnop({
  children,
  onClick,
  variant = 'gevuld',
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'gevuld' | 'omlijnd';
}): JSX.Element {
  const gevuld = variant === 'gevuld';
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded px-5 py-2.5 text-[15px] font-semibold"
      style={{
        border: `1px solid ${TL.primair}`,
        backgroundColor: gevuld ? TL.primair : '#fff',
        color: gevuld ? '#fff' : TL.primair,
      }}
    >
      {children}
    </button>
  );
}

export function TlTabs<T extends string>({
  tabs,
  actief,
  onKies,
}: {
  tabs: { key: T; label: string }[];
  actief: T;
  onKies: (k: T) => void;
}): JSX.Element {
  return (
    <div className="flex gap-9" style={{ borderBottom: `1px solid ${TL.rand}` }}>
      {tabs.map((t) => {
        const on = t.key === actief;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onKies(t.key)}
            className="pb-3 text-[16px]"
            style={{
              color: on ? TL.primair : TL.body,
              boxShadow: on ? `inset 0 -2px 0 0 ${TL.primair}` : undefined,
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

export function TlAvatar({
  initialen,
  grootte = 44,
}: {
  initialen: string;
  grootte?: number;
}): JSX.Element {
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-full font-semibold text-white"
      style={{
        backgroundColor: TL.primair,
        width: grootte,
        height: grootte,
        fontSize: grootte * 0.34,
      }}
    >
      {initialen}
    </span>
  );
}

/** Zwevende assistent rechtsonder; in het portaal een illustratie. */
export function TlAssistent(): JSX.Element {
  return (
    <span
      className="pointer-events-none absolute bottom-6 right-6 flex h-[70px] w-[70px] items-center justify-center rounded-full"
      style={{ backgroundColor: TL.assistent, border: `2px solid ${TL.primair}` }}
    >
      <UserRoundIcon size={32} style={{ color: TL.primair }} />
      <span
        className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold text-white"
        style={{ backgroundColor: TL.oranje }}
      >
        1
      </span>
    </span>
  );
}
